import React, { useState } from 'react';
import styled, { css } from 'styled-components';

interface GridTextAreaProps {
    id: string;
    name?: string;
    /** Shown as the floating label of the field. */
    placeholder: string;
    setRef?:
        | React.RefObject<HTMLTextAreaElement>
        | ((instance: HTMLTextAreaElement | null) => void);
    onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
    onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
    disabled?: boolean;
    validationError?: boolean;
    placeholderRaised?: boolean;
    dataTestId?: string;
    /** Visible lines before the field scrolls. */
    rows?: number;
    defaultValue?: string;
    value?: string;
}

interface StyledLabelProps {
    $isActive: boolean;
    $hasValue: boolean;
    $placeholderRaised?: boolean;
}

interface StyledTextAreaProps {
    $validationError?: boolean;
}

/**
 * A multi-line counterpart of GridInput, with the same floating label and borders, for values such as one URL per
 * line. Unlike TextArea it neither limits the length nor submits the form on Enter.
 */
const GridTextArea: React.FC<GridTextAreaProps> = ({
    id,
    name,
    placeholder,
    setRef,
    onChange,
    onBlur,
    disabled,
    validationError,
    placeholderRaised,
    dataTestId,
    rows = 4,
    defaultValue,
    value,
}: GridTextAreaProps) => {
    const [active, setActive] = useState(false);
    const [hasValue, setHasValue] = useState(!!defaultValue || !!value);

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setHasValue(event.target.value.length > 0);

        if (onChange) onChange(event);
    };

    return (
        <Wrapper>
            <Label
                id={`label-${id}`}
                htmlFor={id}
                $isActive={active}
                $hasValue={hasValue}
                $placeholderRaised={placeholderRaised}
            >
                {placeholder}
            </Label>
            <StyledTextArea
                id={id}
                name={name}
                ref={setRef}
                rows={rows}
                disabled={disabled}
                autoComplete="off"
                spellCheck={false}
                defaultValue={defaultValue}
                value={value}
                onChange={handleChange}
                onFocus={() => setActive(true)}
                onBlur={event => {
                    setActive(false);
                    if (onBlur) onBlur(event);
                }}
                $validationError={validationError}
                data-testid={dataTestId}
            />
        </Wrapper>
    );
};

export default GridTextArea;

const Wrapper = styled.div`
    background-color: ${props => props.theme.colors.light};
    position: relative;
    display: inline-block;
    width: 100%;
`;

const StyledTextArea = styled.textarea<StyledTextAreaProps>`
    display: block;
    background: transparent;
    border-radius: 0;
    border: 0;
    width: 100%;
    min-height: 6rem;
    padding: 3rem 1.6rem 1rem;
    box-sizing: border-box;
    resize: vertical;
    outline: none;
    font: inherit;
    line-height: 1.5;
    color: ${props => props.theme.colors.text.primary};
    color-scheme: ${props => props.theme.mode};

    &:focus {
        border: 0.1rem solid ${props => props.theme.colors.primary};
        outline: none;
    }

    &:user-invalid {
        border: 0.1rem solid ${props => props.theme.colors.error};
    }

    &:disabled {
        opacity: 0.5;
    }

    ${props =>
        props.$validationError &&
        css`
            border: 0.1rem solid ${props.theme.colors.error};
            &:focus {
                border: 0.2rem solid ${props.theme.colors.error};
                outline: none;
            }
        `}
`;

const Label = styled.label<StyledLabelProps>`
    position: absolute;
    font-size: ${props => props.theme.text.size.small};
    transform: translate(1.7rem, 2.3rem) scale(1);
    transform-origin: top left;
    transition: all 0.15s ease-in-out;
    opacity: 0.6;
    pointer-events: none;
    ${props =>
        (props.$isActive || props.$hasValue || props.$placeholderRaised) &&
        css`
            transform: translate(1.7rem, 0.8rem) scale(0.75);
            color: ${props.theme.colors.primary};
            opacity: 1;
        `};
`;
